<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DealsAPI from 'dashboard/api/deals';

defineProps({
  type: { type: String, required: true },
  rows: { type: Array, required: true },
  money: { type: Function, required: true },
});
const emit = defineEmits(['actionCreated']);

const { t } = useI18n();
const router = useRouter();
const loadingActions = ref({});
const messageDialogOpen = ref(false);
const suggestedMessage = ref('');
const messageDealName = ref('');
const openDeal = row =>
  router.push({
    name: 'deal_detail',
    params: { dealId: row.deal_id || row.id },
  });
const priority = value =>
  ({
    high: {
      label: t('COMMERCIAL.REPORTS.INTELLIGENCE.PRIORITY_HIGH'),
      className: 'bg-n-ruby-3 text-n-ruby-11 ring-n-ruby-7',
    },
    medium: {
      label: t('COMMERCIAL.REPORTS.INTELLIGENCE.PRIORITY_MEDIUM'),
      className: 'bg-n-amber-3 text-n-amber-11 ring-n-amber-7',
    },
    low: {
      label: t('COMMERCIAL.REPORTS.INTELLIGENCE.PRIORITY_LOW'),
      className: 'bg-n-blue-3 text-n-blue-11 ring-n-blue-7',
    },
  })[value];
const reason = value =>
  ({
    inactive: t('COMMERCIAL.REPORTS.INTELLIGENCE.REASON_INACTIVE'),
    overdue_activity: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_OVERDUE_ACTIVITY'
    ),
    inactive_and_overdue: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_INACTIVE_AND_OVERDUE'
    ),
    advanced_without_follow_up: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_ADVANCED_WITHOUT_FOLLOW_UP'
    ),
    no_next_activity: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_NO_NEXT_ACTIVITY'
    ),
    scheduled_follow_up: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_SCHEDULED_FOLLOW_UP'
    ),
    advanced_high_value_recent: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_HOT_RECENT'
    ),
    advanced_high_value_upcoming: t(
      'COMMERCIAL.REPORTS.INTELLIGENCE.REASON_HOT_UPCOMING'
    ),
  })[value] || value;
const dueLabel = value =>
  ({
    overdue: t('COMMERCIAL.REPORTS.INTELLIGENCE.DUE_OVERDUE'),
    now: t('COMMERCIAL.REPORTS.INTELLIGENCE.DUE_NOW'),
    scheduled: t('COMMERCIAL.REPORTS.INTELLIGENCE.DUE_SCHEDULED'),
  })[value];
const dealId = row => row.deal_id || row.id;
const actionParams = row => ({
  recommended_action: row.recommended_action,
  action_type: row.action_type || 'follow_up',
  priority: row.priority,
  risk_level: row.risk_level,
});
const loadingKey = (row, action) => `${dealId(row)}-${action}`;
const isActionLoading = (row, action) =>
  Boolean(loadingActions.value[loadingKey(row, action)]);
const withRowLoading = async (row, action, callback) => {
  const key = loadingKey(row, action);
  loadingActions.value[key] = true;
  try {
    await callback();
  } finally {
    loadingActions.value[key] = false;
  }
};
const createFollowUp = row =>
  withRowLoading(row, 'follow-up', async () => {
    try {
      await DealsAPI.createCommercialFollowUp(dealId(row), actionParams(row));
      useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.FOLLOW_UP_CREATED'));
      emit('actionCreated');
    } catch {
      useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.ACTION_ERROR'));
    }
  });
const completeAction = row =>
  withRowLoading(row, 'complete', async () => {
    try {
      await DealsAPI.completeCommercialAction(dealId(row), actionParams(row));
      useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.ACTION_COMPLETED'));
      emit('actionCreated');
    } catch {
      useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.ACTION_ERROR'));
    }
  });
const showMessageSuggestion = row =>
  withRowLoading(row, 'message', async () => {
    try {
      const { data } = await DealsAPI.commercialMessageSuggestion(
        dealId(row),
        actionParams(row)
      );
      suggestedMessage.value = data.payload.message;
      messageDealName.value = row.deal_name || row.name;
      messageDialogOpen.value = true;
    } catch {
      useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.MESSAGE_ERROR'));
    }
  });
const copySuggestedMessage = async () => {
  try {
    await navigator.clipboard.writeText(suggestedMessage.value);
    useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.MESSAGE_COPIED'));
    messageDialogOpen.value = false;
  } catch {
    useAlert(t('COMMERCIAL.REPORTS.INTELLIGENCE.COPY_ERROR'));
  }
};
</script>

<template>
  <div
    class="overflow-x-auto rounded-2xl border border-n-weak bg-n-solid-2 print:overflow-visible print:border-slate-300 print:bg-white"
  >
    <table class="w-full min-w-[880px] text-left text-sm print:min-w-0">
      <thead
        class="border-b border-n-weak bg-n-alpha-2 text-xs text-n-slate-10"
      >
        <tr>
          <th class="px-4 py-3">
            {{
              type === 'actions'
                ? t('COMMERCIAL.REPORTS.INTELLIGENCE.PRIORITY')
                : type === 'risk'
                  ? t('COMMERCIAL.REPORTS.INTELLIGENCE.RISK')
                  : t('COMMERCIAL.REPORTS.INTELLIGENCE.SCORE')
            }}
          </th>
          <th class="px-4 py-3">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.DEAL') }}
          </th>
          <th class="px-4 py-3">{{ t('COMMERCIAL.REPORTS.COLUMNS.STAGE') }}</th>
          <th class="px-4 py-3">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.ADVISOR') }}
          </th>
          <th v-if="type === 'risk'" class="px-4 py-3 text-end">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.INACTIVE_DAYS') }}
          </th>
          <th v-if="type === 'risk'" class="px-4 py-3 text-end">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.OVERDUE') }}
          </th>
          <th v-if="type === 'hot'" class="px-4 py-3 text-end">
            {{ t('COMMERCIAL.REPORTS.COLUMNS.VALUE') }}
          </th>
          <th class="px-4 py-3">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.RECOMMENDED_ACTION') }}
          </th>
          <th class="px-4 py-3">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.REASON') }}
          </th>
          <th v-if="type === 'actions'" class="px-4 py-3">
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.DUE') }}
          </th>
          <th class="px-4 py-3 print:hidden" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.deal_id || row.id"
          class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
        >
          <td class="px-4 py-3">
            <span
              v-if="type !== 'hot'"
              class="inline-flex rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset"
              :class="
                priority(type === 'actions' ? row.priority : row.risk_level)
                  .className
              "
            >
              {{
                priority(type === 'actions' ? row.priority : row.risk_level)
                  .label
              }}
            </span>
            <span
              v-else
              class="inline-flex rounded-full bg-n-teal-3 px-2 py-1 text-xs font-semibold text-n-teal-11 ring-1 ring-inset ring-n-teal-7"
            >
              {{ row.score }}
            </span>
          </td>
          <td class="px-4 py-3">
            <p class="font-medium text-n-slate-12">
              {{ row.deal_name || row.name }}
            </p>
            <p v-if="row.company_name" class="mt-0.5 text-xs text-n-slate-9">
              {{ row.company_name }}
            </p>
          </td>
          <td class="whitespace-nowrap px-4 py-3">{{ row.stage_name }}</td>
          <td class="whitespace-nowrap px-4 py-3">
            {{ row.assigned_agent_name || t('COMMERCIAL.UNASSIGNED') }}
          </td>
          <td v-if="type === 'risk'" class="px-4 py-3 text-end tabular-nums">
            {{ row.days_without_activity }}
          </td>
          <td v-if="type === 'risk'" class="px-4 py-3 text-end tabular-nums">
            {{ row.overdue_activities_count }}
          </td>
          <td
            v-if="type === 'hot'"
            class="whitespace-nowrap px-4 py-3 text-end font-medium"
          >
            {{ money(row.value) }}
          </td>
          <td class="min-w-56 px-4 py-3 font-medium text-n-slate-12">
            {{ row.recommended_action }}
          </td>
          <td class="min-w-56 px-4 py-3 text-n-slate-10">
            {{ reason(row.reason) }}
          </td>
          <td v-if="type === 'actions'" class="whitespace-nowrap px-4 py-3">
            {{ dueLabel(row.due_label) }}
          </td>
          <td class="px-4 py-3 print:hidden">
            <div class="flex min-w-44 flex-col items-stretch gap-1.5">
              <button
                class="inline-flex items-center justify-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-brand hover:bg-n-brand/10"
                @click="openDeal(row)"
              >
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.OPEN_DEAL')
                }}<span class="i-lucide-arrow-up-right size-3.5" />
              </button>
              <button
                class="inline-flex items-center justify-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="isActionLoading(row, 'follow-up')"
                @click="createFollowUp(row)"
              >
                <span class="i-lucide-calendar-plus size-3.5" />
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.CREATE_FOLLOW_UP') }}
              </button>
              <button
                class="inline-flex items-center justify-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="isActionLoading(row, 'message')"
                @click="showMessageSuggestion(row)"
              >
                <span class="i-lucide-copy size-3.5" />
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.COPY_MESSAGE') }}
              </button>
              <button
                class="inline-flex items-center justify-center gap-1 rounded-lg bg-n-teal-9 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-n-teal-10 disabled:opacity-50"
                :disabled="isActionLoading(row, 'complete')"
                @click="completeAction(row)"
              >
                <span class="i-lucide-circle-check size-3.5" />
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.COMPLETE_ACTION') }}
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="!rows.length">
          <td
            :colspan="type === 'risk' ? 9 : 8"
            class="px-4 py-10 text-center text-n-slate-10"
          >
            {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.EMPTY') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div
    v-if="messageDialogOpen"
    class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 print:hidden"
    @click.self="messageDialogOpen = false"
  >
    <section
      class="w-full max-w-lg rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-xl"
    >
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.MESSAGE_TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-n-slate-10">{{ messageDealName }}</p>
      <textarea
        v-model="suggestedMessage"
        rows="6"
        class="mt-4 w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      />
      <div class="mt-4 flex justify-end gap-2">
        <button
          class="rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2"
          @click="messageDialogOpen = false"
        >
          {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.CANCEL') }}
        </button>
        <button
          class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white hover:bg-n-brand-hover"
          @click="copySuggestedMessage"
        >
          <span class="i-lucide-copy size-4" />
          {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.COPY_MESSAGE') }}
        </button>
      </div>
    </section>
  </div>
</template>
